import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { gov } from "@/lib/governanceApi";
import { card, label, input, Pill, SectionTitle, Act, Field, errMsg } from "./ui";

const TEXT = ["title", "processing", "scope", "context", "purpose", "lawful_basis", "necessity", "consultation", "reviewer", "review_date"];
const RISK = { likelihood: ["remote", "possible", "probable"], severity: ["minimal", "significant", "severe"], residual: ["low", "medium", "high"] };

const Risks = ({ risks, set }) => (
  <div data-testid="dpia-risks">
    <div className={label}>Risks & mitigations</div>
    {risks.map((r, i) => (
      <div key={i} className="border border-zinc-200 p-2 mb-2 grid md:grid-cols-[2fr_1fr_1fr_2fr_1fr_auto] gap-2 items-start">
        <textarea rows={2} className={input} placeholder="Risk" value={r.risk || ""} onChange={(e) => set(risks.map((x, j) => (j === i ? { ...x, risk: e.target.value } : x)))} />
        {["likelihood", "severity"].map((k) => (
          <select key={k} className={input} value={r[k] || ""} onChange={(e) => set(risks.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)))}>
            <option value="">{k}</option>{RISK[k].map((o) => <option key={o}>{o}</option>)}
          </select>
        ))}
        <textarea rows={2} className={input} placeholder="Mitigation" value={r.mitigation || ""} onChange={(e) => set(risks.map((x, j) => (j === i ? { ...x, mitigation: e.target.value } : x)))} />
        <select className={input} value={r.residual || ""} onChange={(e) => set(risks.map((x, j) => (j === i ? { ...x, residual: e.target.value } : x)))}>
          <option value="">residual</option>{RISK.residual.map((o) => <option key={o}>{o}</option>)}
        </select>
        <button onClick={() => set(risks.filter((_, j) => j !== i))} className="p-2 text-red-700"><Trash2 size={14} /></button>
      </div>
    ))}
    <Act tone="ghost" onClick={() => set([...risks, {}])} data-testid="dpia-add-risk">Add risk</Act>
  </div>
);

const Screening = ({ rec, criteria, onDone }) => {
  const [f, setF] = useState(rec);
  const [approver, setApprover] = useState("");
  const locked = rec.status === "approved";
  const save = async () => { try { const r = await gov.saveDpia(rec.id, f); setF(r); toast.success(`Saved — result: ${r.result}`); onDone(); } catch (e) { toast.error(errMsg(e)); } };
  const approve = async () => { try { await gov.approveDpia(rec.id, { action: "approve", approver_name: approver }); toast.success("Screening approved and locked"); onDone(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className={`${card} p-4 space-y-3`} data-testid="dpia-screening">
      <div className="flex flex-wrap items-center gap-2"><Pill value={f.status} /><Pill value={f.result} testid="dpia-result" /><span className="text-sm" data-testid="dpia-reason">{f.result_reason}</span></div>
      {locked && <div className="text-xs text-zinc-500">Approved by {rec.approved_by} on {rec.approved_at?.slice(0, 10)} · hash {rec.approved_hash?.slice(0, 16)}… · review {rec.review_date}</div>}
      <fieldset disabled={locked} className="space-y-3">
        <label className="flex items-center gap-2 text-sm font-bold"><input type="checkbox" data-testid="dpia-personal-data" checked={!!f.personal_data} onChange={(e) => setF({ ...f, personal_data: e.target.checked })} /> Personal data is involved</label>
        <div className="grid md:grid-cols-2 gap-1 border border-zinc-200 p-3">
          {criteria.map((c) => (
            <label key={c.key} className="flex items-start gap-2 text-xs">
              <input type="checkbox" data-testid={`dpia-c-${c.key}`} checked={!!f.answers?.[c.key]} onChange={(e) => setF({ ...f, answers: { ...f.answers, [c.key]: e.target.checked } })} />
              <span><span className="font-mono text-[9px] uppercase text-zinc-400 mr-1">{c.group}</span>{c.label}</span>
            </label>
          ))}
        </div>
        <div className="grid md:grid-cols-2 gap-3">{TEXT.map((k) => <Field key={k} name={k} spec={k === "review_date" ? { type: "date" } : { max: ["title", "reviewer"].includes(k) ? 300 : 4000 }} value={f[k]} testid={`dpia-${k}`} onChange={(n, v) => setF({ ...f, [n]: v })} />)}</div>
        <Risks risks={f.risks || []} set={(risks) => setF({ ...f, risks })} />
      </fieldset>
      {!locked && (
        <div className="flex flex-wrap gap-2 items-center">
          <Act onClick={save} data-testid="dpia-save">Save & re-screen</Act>
          <input className={`${input} w-56`} placeholder="Approver name" data-testid="dpia-approver" value={approver} onChange={(e) => setApprover(e.target.value)} />
          <Act tone="ghost" onClick={approve} data-testid="dpia-approve">Approve screening</Act>
          <Act tone="danger" onClick={async () => { try { await gov.archiveDpia(rec.id); toast.success("Screening archived"); onDone(); } catch (e) { toast.error(errMsg(e)); } }} data-testid="dpia-archive">Archive</Act>
        </div>
      )}
    </div>
  );
};

export default function Dpia() {
  const qc = useQueryClient();
  const { data: meta } = useQuery({ queryKey: ["gov-meta"], queryFn: gov.meta });
  const { data = [] } = useQuery({ queryKey: ["gov-dpia"], queryFn: gov.dpia });
  const [sel, setSel] = useState(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["gov-dpia"] });
  const create = async () => { try { const r = await gov.createDpia({ title: "New screening", personal_data: true }); refresh(); setSel(r.id); } catch (e) { toast.error(errMsg(e)); } };
  const current = data.find((d) => d.id === sel);
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-start gap-4">
        <SectionTitle sub="Risk-based screening against UK GDPR Art. 35(3), the ICO's list of high-risk processing and the EDPB/WP248 indicators. Result is calculated server-side. CPH, as controller, remains responsible for its own DPIA.">DPIA screening & risk</SectionTitle>
        <Act onClick={create} data-testid="dpia-new"><Plus size={13} className="mr-1" /> New screening</Act>
      </div>
      <div className="flex flex-wrap gap-2">
        {data.filter((d) => d.status !== "archived").map((d) => (
          <button key={d.id} onClick={() => setSel(d.id)} data-testid={`dpia-item-${d.id}`} className={`text-left text-xs border-2 px-3 py-2 ${sel === d.id ? "border-[#2F6DA3] bg-sky-50" : "border-zinc-200 bg-white"}`}>
            <div className="font-bold">{d.title}</div><div className="flex gap-1 mt-1"><Pill value={d.status} /><Pill value={d.result} /></div>
          </button>
        ))}
      </div>
      {current && meta && <Screening key={current.id + current.updated_at + current.status} rec={current} criteria={meta.dpia_criteria} onDone={refresh} />}
    </div>
  );
}
